import { useEffect, useState } from "react"
import { getMpInstance } from "../utils/mp/sdk"
import { TInstallmentOption } from "../utils/@types/data/installment"

type Params = {
  enabled: boolean
  bin: string // 6 primeiros dígitos do cartão (vazio se incompleto)
  amount: number // em reais
}

type State = {
  options: TInstallmentOption[]
  loading: boolean
  error: string | null
  paymentMethodId: string | null
  issuerId: string | null
}

const empty: State = {
  options: [],
  loading: false,
  error: null,
  paymentMethodId: null,
  issuerId: null,
}

const DEBOUNCE_MS = 400

/**
 * Consulta as condições de parcelamento no Mercado Pago (SDK getInstallments)
 * para o BIN + valor informados. Nada é calculado localmente.
 */
export const useCardInstallments = ({ enabled, bin, amount }: Params) => {
  const [state, setState] = useState<State>(empty)

  useEffect(() => {
    if (!enabled || bin.length < 6 || !(amount > 0)) {
      setState(empty)
      return
    }

    let cancelled = false
    setState({ ...empty, loading: true })

    const timer = setTimeout(async () => {
      const fail = (error: string) => {
        if (!cancelled) setState({ ...empty, error })
      }

      try {
        const mpRes = await getMpInstance()
        if (!mpRes.ok) return fail(mpRes.error)

        const res = await mpRes.mp.getInstallments({
          amount: amount.toFixed(2),
          bin,
          paymentTypeId: "credit_card",
        })

        // A doc oficial usa res[0].payer_costs; find() é só uma versão
        // defensiva para o caso de a primeira entrada vir sem parcelas.
        const entry = res?.find((r) => r.payer_costs?.length)
        if (!entry) {
          return fail("Não há parcelamento disponível para este cartão.")
        }

        const options: TInstallmentOption[] = entry.payer_costs.map((c) => ({
          installments: c.installments,
          installmentAmount: c.installment_amount,
          totalAmount: c.total_amount,
          installmentRate: c.installment_rate,
          message: c.recommended_message,
        }))

        if (!cancelled)
          setState({
            options,
            loading: false,
            error: null,
            paymentMethodId: entry.payment_method_id,
            issuerId: entry.issuer?.id != null ? String(entry.issuer.id) : null,
          })
      } catch {
        fail("Não foi possível consultar as parcelas. Tente novamente.")
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [enabled, bin, amount])

  return state
}
